<template>

  <div class="position-relative">

    <v-overlay v-if="loading" :model-value="true" contained persistent class="align-center justify-center">
      <v-progress-circular indeterminate />
    </v-overlay>

    <v-calendar
      ref="calendar"
      v-bind="$attrs"
      :model-value="vuetifyModel"
      @update:model-value="handleUpdate"
    >

      <!-- pass all slots through -->
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>

    </v-calendar>

  </div>

</template>

<script setup lang="ts">

defineOptions({
  inheritAttrs: false
})

/**
 * TODO: Explain the purpose of this component.
 */

import { computed, useTemplateRef } from 'vue'
import { VCalendar } from 'vuetify/components'
import { Temporal } from '@js-temporal/polyfill'

interface Props {
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  loading: false,
})

type VCalendarType = InstanceType<typeof VCalendar>
const calendarRef = useTemplateRef<VCalendarType>('calendar')

const model = defineModel<Temporal.PlainDate>({ required: true })
const $emit = defineEmits(['update:model-value'])

const vuetifyModel = computed<string>(() => {
  return model.value.toString()
})

function handleUpdate(newValue: string) {
  // Because we set VCalendar's :model-value as a string, we assume that it will emit newValue as a string.
  // Technically, though, it could be a number or a Date - so we might need to handle those cases too.
  return $emit('update:model-value', Temporal.PlainDate.from(newValue))
}

/**
 * VCalendar's firstDayOfWeek prop can be either a string or a number. Normalize to a number (0 = Sunday).
 */
const firstDayOfWeek = computed<null | number>(() => {
  if (!calendarRef.value) {
    return null
  }
  return calendarRef.value.firstDayOfWeek ? parseInt(calendarRef.value.firstDayOfWeek.toString(), 10) : 0
})

const lastDayOfWeek = computed<null | number>(() => {
  if (firstDayOfWeek.value === null) {
    return null
  }
  return (firstDayOfWeek.value + 6) % 7
})

/**
 * Exposed values
 */

const temporalStart = computed<null | Temporal.PlainDate>(() => {
  return calendarRef.value === null ? null : normalizeStartOrEnd(calendarRef.value.start)
})

const temporalEnd = computed<null | Temporal.PlainDate>(() => {
  const output = calendarRef.value === null ? null : normalizeStartOrEnd(calendarRef.value.end)
  return output
})

const monthStart = computed<null | Temporal.PlainDate>(() => {

  if (!firstDayOfWeek.value) {
    return null
  }

  const firstOfMonth = model.value.with({day: 1})
  const firstOfMonthDow = normalizeTemporalDayOfWeek(firstOfMonth.dayOfWeek)
  let daysToRewind = firstOfMonthDow - firstDayOfWeek.value
  if (daysToRewind < 0) {
    daysToRewind += 7
  }
  return firstOfMonth.subtract({days: daysToRewind})

})

const monthEnd = computed<null | Temporal.PlainDate>(() => {

  if (lastDayOfWeek.value === null) {
    return null
  }

  const lastOfMonth = model.value.with({day: model.value.daysInMonth})
  const lastOfMonthDow = normalizeTemporalDayOfWeek(lastOfMonth.dayOfWeek)
  let daysToAdvance = lastDayOfWeek.value - lastOfMonthDow
  if (daysToAdvance < 0) {
    daysToAdvance += 7
  }

  return lastOfMonth.add({days: daysToAdvance})

})

const fourDayStart = computed<null | Temporal.PlainDate>(() => {
  return model.value
})

const fourDayEnd = computed<null | Temporal.PlainDate>(() => {
  return model.value.add({days: 3})
})

const prev = computed(() => {
  return calendarRef.value?.prev
})

const next = computed(() => {
  return calendarRef.value?.next
})

const title = computed<null | string>(() => calendarRef.value?.title ?? null)

defineExpose({
  start: temporalStart,
  end: temporalEnd,

  /**
   * First date visible in the "month" calendar type.
   */
  monthStart: monthStart,

  /**
   * Last date visible in the "month" calendar type.
   */
  monthEnd: monthEnd,

  /**
   * First date visible in the "4day" calendar type.
   */
  fourDayStart: fourDayStart,

  /**
   * Last date visible in the "4day" calendar type.
   */
  fourDayEnd: fourDayEnd,

  prev: prev,
  next: next,
  title: title,
})

/**
 * Miscellaneous
 */

function normalizeStartOrEnd(value: undefined | string | number | Date): null | Temporal.PlainDate {

  if (value === undefined) {
    return null
  }

  if (value instanceof Date) {
    return new Temporal.PlainDate(value.getFullYear(), value.getMonth() + 1, value.getDate())
  }

  if (typeof value === "string") {
    return Temporal.PlainDate.from(value)
  }

  // bound is presumably a number
  throw new Error("unimplemented: need to figure out how to interpret a numeric calendar start or end")

}

/**
 * Temporal numbers days of week as 1 = Monday, 2 = Tuesday, ... 7 = Sunday. This function converts to sane values.
 */
function normalizeTemporalDayOfWeek(temporalDow: number): number {
  return temporalDow === 7 ? 0 : temporalDow
}

</script>
